/**
 * /marketing/lists/:id — one list.
 *
 * A SEGMENT is edited as a definition with a live preview BELOW it (both full
 * width, owner's decision 2026-10-03); nothing is saved until Save is pressed,
 * and the preview reads the DRAFT — the rules AND the hand exclusions — so the
 * count on screen is always the count of what would be saved.
 *
 * Hand exclusions are a DRAFT set of person records (`type:id`, never an email),
 * seeded from `list.excludedMembers`, part of dirtiness, and sent in the Save
 * PATCH as `excludedMembers` only when the SET differs. It is a whole-set
 * replace: `[]` clears every exclusion, absent leaves them alone.
 *
 * A STATIC list is its members: add contacts, import a CSV, remove selected.
 *
 * ⚠️ The preview has ONE debounced request path (rules, exclusions, page,
 * search, Show) and each change ABORTS the previous request; a sequence number
 * backs that up, so a slow answer for an older draft can never overwrite the
 * newer one.
 *
 * ── Saving and leaving ────────────────────────────────────────────────────
 * Save lives in a STICKY BOTTOM BAR shown only while there are unsaved changes
 * ("Unsaved changes · Discard · Save"); Discard restores the last saved name,
 * description, definition and exclusions. `beforeunload` covers a reload or a
 * closed tab, and this page's own "← Lists" link asks first. ⚠️ A sidebar link
 * is NOT intercepted — this app runs under a plain `BrowserRouter`, where
 * `useBlocker` is unavailable. Same limit `TemplateEditorPage` records.
 *
 * The bar is `sticky bottom-0` and IN FLOW at the end of the editor, so at the
 * end of the scroll it sits below the last row rather than over it. It is on
 * rung 20 — the page-content ceiling of the layer ladder in `src/index.css`,
 * under the app chrome at 30.
 *
 * ⚠️ A rule-less segment matches EVERYONE in its audience, so saving one asks
 * first, naming the count the preview reported.
 *
 * `?new=1` (set by the New list modal for a segment) seeds ONE blank rule on
 * the first field, then the key is removed. The untouched seed is not counted
 * as an unsaved change — nobody has edited anything yet.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Badge,
  Button,
  ConfirmDialog,
  EmptyState,
  Field,
  Input,
  PageHeader,
  Panel,
  PanelBody,
  PanelHeader,
  Skeleton,
  Textarea,
} from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { TextLink } from '@/components/ui/TextLink';
import { AddContactsModal } from '@/components/marketing/lists/AddContactsModal';
import { ImportSubscribersModal } from '@/components/marketing/lists/ImportSubscribersModal';
import { ListMembersTable } from '@/components/marketing/lists/ListMembersTable';
import { SegmentBuilder } from '@/components/marketing/lists/SegmentBuilder';
import { SegmentPreviewTable } from '@/components/marketing/lists/SegmentPreviewTable';
import { defaultRuleFor } from '@/components/marketing/lists/ref-options';
import { api, ApiError } from '@/lib/api';
import { saveBlob } from '@/lib/download';
import { formatDateTime } from '@/lib/format';
import { useAuth } from '@/lib/use-auth';
import type {
  ListPreview,
  ListPreviewShow,
  MarketingList,
  MarketingListAudience,
  MarketingListUpdateInput,
  MarketingMemberRef,
  MarketingSegmentPreviewInput,
  SegmentDefinition,
  SegmentField,
} from '@/lib/types';

const AUDIENCE_LABEL: Record<MarketingListAudience, string> = {
  contacts: 'Contacts',
  subscribers: 'Subscribers',
  mixed: 'Contacts and subscribers',
};

const PREVIEW_DEBOUNCE_MS = 300;
const PREVIEW_PER_PAGE = 50;
const EMPTY_DEFINITION: SegmentDefinition = { match: 'all', rules: [] };

/** `type:id` — the same key `SegmentPreviewTable` reads the draft set by. */
const memberKey = (m: { type: string; id: number }) => `${m.type}:${m.id}`;

/** `type:id` keys, sorted, so two sets compare by content and not by order. */
function sortedKeys(refs: readonly MarketingMemberRef[]): string[] {
  return [...new Set(refs.map(memberKey))].sort();
}

function refOfKey(key: string): MarketingMemberRef {
  const [type, id] = key.split(':');
  return { type: type === 'subscriber' ? 'subscriber' : 'contact', id: Number(id) };
}

function describe(err: unknown, fallback: string): string {
  return err instanceof ApiError ? (err.formMessage ?? err.message) : fallback;
}

export function ListDetailPage() {
  const params = useParams();
  const id = Number(params.id);
  const { ability } = useAuth();
  const canRead = ability.canRead('marketing-lists');
  const canUpdate = ability.canUpdate('marketing-lists');
  const canExport = ability.canExport('marketing-lists');

  const [list, setList] = useState<MarketingList | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<{ status: number | null; message: string } | null>(null);

  const [refreshing, setRefreshing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [headerError, setHeaderError] = useState<string | null>(null);

  const navigate = useNavigate();
  /** Reported up by whichever editor is mounted; drives both leave guards. */
  const [dirty, setDirty] = useState(false);
  const [leaveOpen, setLeaveOpen] = useState(false);

  // Reload or closed tab while there is unsaved work.
  useEffect(() => {
    if (!dirty) return;
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  useEffect(() => {
    // Both of these render their own state below without ever reading `loading`.
    if (!Number.isInteger(id) || id <= 0 || !canRead) return;
    const controller = new AbortController();
    setLoading(true);
    setLoadError(null);
    api
      .getMarketingList(id, controller.signal)
      .then((response) => {
        if (controller.signal.aborted) return;
        setList(response.data);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setLoadError({
          status: err instanceof ApiError ? err.status : null,
          message: describe(err, 'The list could not be loaded.'),
        });
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [id, canRead]);

  const refreshCount = async () => {
    if (!list || refreshing) return;
    setRefreshing(true);
    setHeaderError(null);
    try {
      const response = await api.refreshMarketingListCount(list.id);
      setList(response.data);
    } catch (err) {
      setHeaderError(describe(err, 'The count could not be refreshed.'));
    } finally {
      setRefreshing(false);
    }
  };

  const exportCsv = async () => {
    if (!list || exporting) return;
    setExporting(true);
    setHeaderError(null);
    try {
      const { filename, blob } = await api.exportMarketingList(list.id);
      saveBlob(filename, blob);
    } catch (err) {
      setHeaderError(describe(err, 'The export failed.'));
    } finally {
      setExporting(false);
    }
  };

  const back = (
    <TextLink
      to="/marketing/lists"
      tone="muted"
      className="text-sm"
      onClick={(event) => {
        // A modified click (new tab, new window) leaves this page alone.
        if (!dirty || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
        event.preventDefault();
        setLeaveOpen(true);
      }}
    >
      ← Lists
    </TextLink>
  );

  if (!canRead) {
    return (
      <div className="grid min-w-0 grid-cols-[minmax(0,1fr)] gap-6">
        {back}
        <EmptyState
          title="Lists are restricted"
          description="Your permission set does not include this. An administrator can change that."
        />
      </div>
    );
  }

  if (!Number.isInteger(id) || id <= 0 || loadError?.status === 404) {
    return (
      <div className="grid min-w-0 grid-cols-[minmax(0,1fr)] gap-6">
        {back}
        <EmptyState title="List not found" description="It may have been deleted." />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="grid min-w-0 grid-cols-[minmax(0,1fr)] gap-6">
        {back}
        <Notice tone="danger" role="alert">
          {loadError.message}
        </Notice>
      </div>
    );
  }

  if (loading || !list) {
    return (
      <div className="grid min-w-0 grid-cols-[minmax(0,1fr)] gap-6">
        {back}
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="grid min-w-0 grid-cols-[minmax(0,1fr)] gap-6">
      {back}
      <PageHeader
        eyebrow="B2B Marketing list"
        title={list.name}
        description={
          <span className="flex flex-wrap items-center gap-2">
            <Badge tone="neutral" variant="outline">
              {list.kind === 'segment' ? 'Live' : 'Static'}
            </Badge>
            <span>{AUDIENCE_LABEL[list.audience]}</span>
            {list.isArchived && <Badge tone="warning">Archived</Badge>}
            <span className="text-fg-muted">·</span>
            <span className="tabular">
              {list.memberCount === null ? 'Not counted' : `${list.memberCount.toLocaleString()} members`}
              {list.kind === 'segment' && list.lastCountedAt ? `, counted ${formatDateTime(list.lastCountedAt)}` : ''}
            </span>
          </span>
        }
        actions={
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" size="sm" onClick={refreshCount} loading={refreshing}>
              Refresh count
            </Button>
            {canExport && (
              <Button variant="secondary" size="sm" onClick={exportCsv} loading={exporting}>
                Export CSV
              </Button>
            )}
          </div>
        }
      />

      {headerError && (
        <Notice tone="danger" role="alert">
          {headerError}
        </Notice>
      )}

      {list.kind === 'segment' ? (
        <SegmentEditor list={list} canUpdate={canUpdate} onSaved={setList} onDirtyChange={setDirty} />
      ) : (
        <StaticEditor
          list={list}
          canUpdate={canUpdate}
          onSaved={setList}
          onMembersChanged={refreshCount}
          onDirtyChange={setDirty}
        />
      )}

      <ConfirmDialog
        open={leaveOpen}
        onClose={() => setLeaveOpen(false)}
        onConfirm={() => {
          setLeaveOpen(false);
          navigate('/marketing/lists');
        }}
        tone="danger"
        title="Leave without saving?"
        description="Your changes to this list have not been saved and will be lost."
        confirmLabel="Leave"
        cancelLabel="Keep editing"
      />
    </div>
  );
}

/* ---- Unsaved-changes bar --------------------------------------------------- */

/**
 * Shown only while there is something to save. `sticky bottom-0` and in flow at
 * the end of the editor, so it never sits over the last row once scrolled to the
 * bottom; rung 20 is the page-content ceiling, under the app chrome at 30.
 */
function UnsavedBar({
  saving,
  onDiscard,
  onSave,
}: {
  saving: boolean;
  onDiscard(): void;
  onSave(): void;
}) {
  return (
    <div
      role="region"
      aria-label="Unsaved changes"
      className="sticky bottom-0 z-20 flex min-w-0 flex-wrap items-center justify-between gap-2 rounded-md border border-hairline-strong bg-surface-raised px-3 py-2 shadow-md"
    >
      <span className="text-sm font-medium text-fg">Unsaved changes</span>
      <span className="flex flex-wrap gap-2">
        <Button variant="ghost" size="sm" onClick={onDiscard} disabled={saving}>
          Discard
        </Button>
        <Button variant="primary" size="sm" onClick={onSave} loading={saving}>
          Save
        </Button>
      </span>
    </div>
  );
}

/** Reports `dirty` to the page so its leave guards know; clears on unmount. */
function useReportDirty(dirty: boolean, onDirtyChange: (dirty: boolean) => void) {
  useEffect(() => {
    onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);
  useEffect(() => () => onDirtyChange(false), [onDirtyChange]);
}

/* ---- Name and description ------------------------------------------------- */

function DetailsFields({
  name,
  description,
  onName,
  onDescription,
  disabled,
  errors,
}: {
  name: string;
  description: string;
  onName(v: string): void;
  onDescription(v: string): void;
  disabled: boolean;
  errors: Record<string, string>;
}) {
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <Field label="Name" required error={errors.name}>
        {(props) => (
          <Input
            {...props}
            value={name}
            maxLength={160}
            onChange={(e) => onName(e.target.value)}
            disabled={disabled}
            invalid={Boolean(errors.name)}
          />
        )}
      </Field>
      <Field label="Description" error={errors.description}>
        {(props) => (
          <Textarea
            {...props}
            rows={1}
            value={description}
            maxLength={500}
            onChange={(e) => onDescription(e.target.value)}
            disabled={disabled}
          />
        )}
      </Field>
    </div>
  );
}

function detailsPatch(list: MarketingList, name: string, description: string): MarketingListUpdateInput {
  const patch: MarketingListUpdateInput = {};
  if (name.trim() !== list.name) patch.name = name.trim();
  const desc = description.trim() === '' ? null : description.trim();
  if (desc !== list.description) patch.description = desc;
  return patch;
}

function errorsOf(err: unknown): { fields: Record<string, string>; form: string | null } {
  if (err instanceof ApiError) {
    const fields = err.fieldErrors;
    const form = err.formMessages.length > 0 ? err.formMessages.join(' ') : Object.keys(fields).length > 0 ? null : (err.formMessage ?? err.message);
    return { fields, form };
  }
  return { fields: {}, form: 'The list could not be saved.' };
}

/* ---- Segment --------------------------------------------------------------- */

function SegmentEditor({
  list,
  canUpdate,
  onSaved,
  onDirtyChange,
}: {
  list: MarketingList;
  canUpdate: boolean;
  onSaved(list: MarketingList): void;
  onDirtyChange(dirty: boolean): void;
}) {
  const audience = list.audience === 'subscribers' ? 'subscribers' : 'contacts';
  const [fields, setFields] = useState<SegmentField[] | null>(null);
  const [fieldsError, setFieldsError] = useState<string | null>(null);

  const [name, setName] = useState(list.name);
  const [description, setDescription] = useState(list.description ?? '');
  const [definition, setDefinition] = useState<SegmentDefinition>(list.definition ?? EMPTY_DEFINITION);

  const [saving, setSaving] = useState(false);
  const [saveErrors, setSaveErrors] = useState<Record<string, string>>({});
  const [saveFormError, setSaveFormError] = useState<string | null>(null);
  const [savedNotice, setSavedNotice] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    api
      .listSegmentFields(controller.signal)
      .then((response) => {
        if (controller.signal.aborted) return;
        setFields(response.data[audience]);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setFieldsError(describe(err, 'The segment fields could not be loaded.'));
      });
    return () => controller.abort();
  }, [audience]);

  /* -- the DRAFT hand-exclusion set, keyed `type:id` -- */
  // The saved baseline moves only when a response REPORTS the set: a list read
  // that omits `excludedMembers` (e.g. a count refresh) is "not reported", and
  // treating it as `[]` would make every exclusion look like an unsaved edit.
  const [savedExcludedKeys, setSavedExcludedKeys] = useState(() => sortedKeys(list.excludedMembers ?? []));
  const [seenExcludedMembers, setSeenExcludedMembers] = useState(list.excludedMembers);
  if (list.excludedMembers !== seenExcludedMembers) {
    setSeenExcludedMembers(list.excludedMembers);
    if (list.excludedMembers) setSavedExcludedKeys(sortedKeys(list.excludedMembers));
  }
  const [excluded, setExcluded] = useState<ReadonlySet<string>>(() => new Set(savedExcludedKeys));
  const excludedKeys = useMemo(() => [...excluded].sort(), [excluded]);
  const exclusionsDirty = excludedKeys.join(',') !== savedExcludedKeys.join(',');

  /* -- `?new=1`: seed ONE blank rule on the first field, then drop the key -- */
  const [searchParams, setSearchParams] = useSearchParams();
  const isNew = searchParams.get('new') === '1';
  /** The seeded definition, exactly — while the draft still equals it, it is not an edit. */
  const [pristineSeed, setPristineSeed] = useState<string | null>(null);
  // Seeded during render (the adjust-state-on-change pattern used for the
  // exclusions above), once, after the field catalogue arrives.
  const [seedDone, setSeedDone] = useState(false);
  if (isNew && !seedDone && fields !== null) {
    setSeedDone(true);
    const first = fields[0];
    if ((list.definition?.rules.length ?? 0) === 0 && definition.rules.length === 0 && first) {
      const next: SegmentDefinition = { ...definition, rules: [defaultRuleFor(first)] };
      setDefinition(next);
      setPristineSeed(JSON.stringify(next));
    }
  }
  // Drop `new` from the URL once the seed has been considered — a navigation, so an effect.
  useEffect(() => {
    if (!isNew || !seedDone) return;
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        p.delete('new');
        return p;
      },
      { replace: true },
    );
  }, [isNew, seedDone, setSearchParams]);

  const savedDefinition = JSON.stringify(list.definition ?? EMPTY_DEFINITION);
  const draftDefinition = JSON.stringify(definition);
  const definitionDirty = draftDefinition !== savedDefinition && draftDefinition !== pristineSeed;
  const patch = detailsPatch(list, name, description);
  if (definitionDirty) patch.definition = definition;
  if (exclusionsDirty) patch.excludedMembers = excludedKeys.map(refOfKey);
  const dirty = Object.keys(patch).length > 0;
  useReportDirty(dirty, onDirtyChange);
  /** What the definition will be after Save: the draft if it moves, else what is stored. */
  const resultingRuleCount = definitionDirty
    ? definition.rules.length
    : (list.definition ?? EMPTY_DEFINITION).rules.length;

  /* -- live preview of the DRAFT -- */
  const [preview, setPreview] = useState<ListPreview | null>(null);
  /** The request the preview on screen answers — the select-all reads it. */
  const [appliedRequest, setAppliedRequest] = useState<MarketingSegmentPreviewInput | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewFieldErrors, setPreviewFieldErrors] = useState<Record<string, string>>({});
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [show, setShow] = useState<ListPreviewShow>('all');
  const sequence = useRef(0);

  const trimmedSearch = search.trim();
  const requestKey = JSON.stringify({
    audience,
    definition,
    excludedMembers: excludedKeys.map(refOfKey),
    page,
    perPage: PREVIEW_PER_PAGE,
    ...(trimmedSearch ? { search: trimmedSearch } : {}),
    show,
  } satisfies MarketingSegmentPreviewInput);

  const runPreview = useCallback((body: MarketingSegmentPreviewInput, signal: AbortSignal) => {
    const mine = ++sequence.current;
    setPreviewLoading(true);
    api
      .previewMarketingSegment(body, signal)
      .then((response) => {
        if (mine !== sequence.current) return;
        setPreview(response.data);
        setAppliedRequest(body);
        setPreviewError(null);
        setPreviewFieldErrors({});
      })
      .catch((err: unknown) => {
        if (signal.aborted || mine !== sequence.current) return;
        if (err instanceof ApiError && err.status === 422) {
          setPreviewFieldErrors(err.fieldErrors);
          setPreviewError(err.formMessages.join(' ') || 'Finish the highlighted rules to see a preview.');
        } else {
          setPreviewFieldErrors({});
          setPreviewError(describe(err, 'The preview could not be loaded.'));
        }
      })
      .finally(() => {
        if (mine === sequence.current) setPreviewLoading(false);
      });
  }, []);

  useEffect(() => {
    const body = JSON.parse(requestKey) as MarketingSegmentPreviewInput;
    const controller = new AbortController();
    const handle = window.setTimeout(() => runPreview(body, controller.signal), PREVIEW_DEBOUNCE_MS);
    return () => {
      window.clearTimeout(handle);
      controller.abort();
    };
  }, [requestKey, runPreview]);

  /** Every ref matching the preview on screen — for the header select-all. */
  const loadMatchingRefs = useCallback(
    async (signal: AbortSignal): Promise<MarketingMemberRef[]> => {
      if (!appliedRequest) return [];
      const response = await api.previewMarketingSegment({ ...appliedRequest, refsOnly: true }, signal);
      if (!response.data.refs) throw new Error('refs missing');
      return response.data.refs;
    },
    [appliedRequest],
  );

  const toggleMember = (ref: MarketingMemberRef, include: boolean) => {
    setExcluded((prev) => {
      const next = new Set(prev);
      if (include) next.delete(memberKey(ref));
      else next.add(memberKey(ref));
      return next;
    });
    setSavedNotice(false);
  };

  const applyMany = (refs: MarketingMemberRef[], include: boolean) => {
    setExcluded((prev) => {
      const next = new Set(prev);
      for (const ref of refs) {
        if (include) next.delete(memberKey(ref));
        else next.add(memberKey(ref));
      }
      return next;
    });
    setSavedNotice(false);
  };

  const [ruleLessOpen, setRuleLessOpen] = useState(false);
  /**
   * The count to name in the rule-less confirmation — only when the preview on
   * screen really answers "no rules, these exclusions"; otherwise `null`, and the
   * dialog says "everyone" without inventing a number.
   */
  const ruleLessCount =
    preview !== null &&
    appliedRequest !== null &&
    appliedRequest.definition.rules.length === 0 &&
    definition.rules.length === 0 &&
    sortedKeys(appliedRequest.excludedMembers ?? []).join(',') === excludedKeys.join(',')
      ? preview.count
      : null;

  /** Save, asking first when the result would be a segment with no rules. */
  const requestSave = () => {
    if (!dirty || saving) return;
    if (patch.name !== undefined && !patch.name) {
      setSaveErrors({ name: 'A list needs a name.' });
      return;
    }
    if (resultingRuleCount === 0) {
      setRuleLessOpen(true);
      return;
    }
    void save();
  };

  const discard = () => {
    setName(list.name);
    setDescription(list.description ?? '');
    setDefinition(list.definition ?? EMPTY_DEFINITION);
    setPristineSeed(null);
    setExcluded(new Set(savedExcludedKeys));
    setSaveErrors({});
    setSaveFormError(null);
    setSavedNotice(false);
    setPage(1);
  };

  const save = async () => {
    if (!dirty || saving) return;
    setSaving(true);
    setSaveErrors({});
    setSaveFormError(null);
    setSavedNotice(false);
    try {
      const response = await api.updateMarketingList(list.id, patch);
      onSaved(response.data);
      setDefinition(response.data.definition ?? EMPTY_DEFINITION);
      setName(response.data.name);
      setDescription(response.data.description ?? '');
      const savedExcluded = response.data.excludedMembers;
      if (savedExcluded) setExcluded(new Set(sortedKeys(savedExcluded)));
      setSavedNotice(true);
    } catch (err) {
      const { fields: fe, form } = errorsOf(err);
      // An exclusion refusal has no control to mark — it goes in the banner.
      const exclusionMessages = Object.entries(fe)
        .filter(([path]) => path === 'excludedMembers' || path.startsWith('excludedMembers.'))
        .map(([, message]) => message);
      setSaveErrors(fe);
      setSaveFormError(
        exclusionMessages.length > 0 ? [form, ...new Set(exclusionMessages)].filter(Boolean).join(' ') : form,
      );
    } finally {
      setSaving(false);
    }
  };

  const builderErrors = { ...previewFieldErrors, ...saveErrors };
  const readOnly = !canUpdate;

  return (
    <div className="grid min-w-0 grid-cols-[minmax(0,1fr)] gap-6">
      <Panel className="min-w-0">
        <PanelHeader title="Definition" />
        <PanelBody className="grid gap-4">
          {saveFormError && (
            <Notice tone="danger" role="alert">
              {saveFormError}
            </Notice>
          )}
          {savedNotice && !dirty && (
            <p role="status" className="m-0 text-sm text-success">
              Saved.
            </p>
          )}
          <DetailsFields
            name={name}
            description={description}
            onName={setName}
            onDescription={setDescription}
            disabled={readOnly || saving}
            errors={saveErrors}
          />
          {fieldsError ? (
            <Notice tone="danger" role="alert">
              {fieldsError}
            </Notice>
          ) : fields === null ? (
            <div className="grid gap-2">
              <Skeleton className="h-9 w-full" />
              <Skeleton className="h-9 w-full" />
              <Skeleton className="h-9 w-2/3" />
            </div>
          ) : (
            <SegmentBuilder
              audience={audience}
              value={definition}
              onChange={(next) => {
                setDefinition(next);
                // Any touch makes the draft the reader's own — compare it to what is saved from here on.
                setPristineSeed(null);
                setPage(1);
                setSavedNotice(false);
              }}
              fields={fields}
              disabled={readOnly || saving}
              errors={builderErrors}
            />
          )}
        </PanelBody>
      </Panel>

      <SegmentPreviewTable
        preview={preview}
        loading={previewLoading}
        error={previewError}
        excluded={excluded}
        search={search}
        onSearch={(value) => {
          setSearch(value);
          setPage(1);
        }}
        appliedSearch={appliedRequest?.search ?? ''}
        show={show}
        onShow={(value) => {
          setShow(value);
          setPage(1);
        }}
        appliedShow={appliedRequest?.show ?? 'all'}
        page={page}
        perPage={PREVIEW_PER_PAGE}
        onPage={setPage}
        readOnly={readOnly || saving}
        onToggle={toggleMember}
        onApplyMany={applyMany}
        loadMatchingRefs={loadMatchingRefs}
        matchesEveryone={definition.rules.length === 0}
      />

      {canUpdate && dirty && <UnsavedBar saving={saving} onDiscard={discard} onSave={requestSave} />}

      <ConfirmDialog
        open={ruleLessOpen}
        onClose={() => setRuleLessOpen(false)}
        onConfirm={() => {
          setRuleLessOpen(false);
          void save();
        }}
        title="Save a segment with no rules?"
        description={
          ruleLessCount === null
            ? 'With no rules, this segment matches everyone in its audience.'
            : `With no rules, this segment matches everyone in its audience — ${ruleLessCount.toLocaleString()} ${
                ruleLessCount === 1 ? 'person' : 'people'
              } in the latest preview.`
        }
        confirmLabel="Save anyway"
        cancelLabel="Keep editing"
      />
    </div>
  );
}

/* ---- Static ---------------------------------------------------------------- */

function StaticEditor({
  list,
  canUpdate,
  onSaved,
  onMembersChanged,
  onDirtyChange,
}: {
  list: MarketingList;
  canUpdate: boolean;
  onSaved(list: MarketingList): void;
  onMembersChanged(): void;
  onDirtyChange(dirty: boolean): void;
}) {
  const [name, setName] = useState(list.name);
  const [description, setDescription] = useState(list.description ?? '');
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);

  const [reloadKey, setReloadKey] = useState(0);
  const [addOpen, setAddOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const patch = detailsPatch(list, name, description);
  const dirty = Object.keys(patch).length > 0;
  useReportDirty(dirty, onDirtyChange);

  const discard = () => {
    setName(list.name);
    setDescription(list.description ?? '');
    setErrors({});
    setFormError(null);
  };

  const save = async () => {
    if (!dirty || saving) return;
    if (patch.name !== undefined && !patch.name) {
      setErrors({ name: 'A list needs a name.' });
      return;
    }
    setSaving(true);
    setErrors({});
    setFormError(null);
    try {
      const response = await api.updateMarketingList(list.id, patch);
      onSaved(response.data);
      setName(response.data.name);
      setDescription(response.data.description ?? '');
    } catch (err) {
      const { fields, form } = errorsOf(err);
      setErrors(fields);
      setFormError(form);
    } finally {
      setSaving(false);
    }
  };

  const membersChanged = () => {
    setReloadKey((n) => n + 1);
    onMembersChanged();
  };

  return (
    <div className="grid min-w-0 grid-cols-[minmax(0,1fr)] gap-6">
      {canUpdate && (
        <Panel>
          <PanelHeader title="Details" />
          <PanelBody className="grid gap-4">
            {formError && (
              <Notice tone="danger" role="alert">
                {formError}
              </Notice>
            )}
            <DetailsFields
              name={name}
              description={description}
              onName={setName}
              onDescription={setDescription}
              disabled={saving}
              errors={errors}
            />
          </PanelBody>
        </Panel>
      )}

      {notice && (
        <Notice tone="success" role="status">
          {notice}
        </Notice>
      )}

      <ListMembersTable
        listId={list.id}
        canRemove={canUpdate}
        reloadKey={reloadKey}
        onChanged={onMembersChanged}
        onAddContacts={canUpdate ? () => setAddOpen(true) : undefined}
        onImport={canUpdate ? () => setImportOpen(true) : undefined}
        actions={
          canUpdate ? (
            <>
              <Button variant="secondary" size="sm" onClick={() => setAddOpen(true)}>
                Add contacts
              </Button>
              <Button variant="secondary" size="sm" onClick={() => setImportOpen(true)}>
                Import CSV
              </Button>
            </>
          ) : undefined
        }
      />

      <AddContactsModal
        open={addOpen}
        listId={list.id}
        onClose={() => setAddOpen(false)}
        onAdded={({ added, alreadyPresent }) => {
          setAddOpen(false);
          setNotice(
            `${added} ${added === 1 ? 'contact' : 'contacts'} added${
              alreadyPresent > 0 ? `, ${alreadyPresent} already in this list` : ''
            }.`,
          );
          membersChanged();
        }}
      />

      <ImportSubscribersModal
        open={importOpen}
        listId={list.id}
        onClose={() => setImportOpen(false)}
        onImported={membersChanged}
      />

      {canUpdate && dirty && <UnsavedBar saving={saving} onDiscard={discard} onSave={() => void save()} />}
    </div>
  );
}
